import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import FormControl from 'react-bootstrap/FormControl';

import { displayErrorModal } from '../../../helpers/swal';
import { login, generateOTP as generateOTPApi } from '../../../helpers/api';
import { setUserData } from '../../../store/actions/authentication';
import env from '../../../config/env';

import style from '../style.module.scss';

const windowSize = parseInt(process.env.OTP_TIMEOUT_SECONDS, 10) || 60;

function formatSecondsToMMSS(totalSeconds) {
  if (totalSeconds < 60) {
    return totalSeconds.toString();
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const LoginForm = ({ setUserDataAction }) => {
  const [formData, setFormData] = useState({ username: '', password: '', otp: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [successTimestamp, setSuccessTimestamp] = useState();
  const [countdown, setCountdown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    let timer;
    if (showOTP && successTimestamp) {
      timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - successTimestamp) / 1000);

        setCountdown(windowSize - elapsed);

        if (elapsed >= windowSize) {
          clearInterval(timer);
          setSuccessTimestamp();
          setCountdown(0);
        }
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [showOTP, successTimestamp]);

  const generateOTP = async () => {
    if (!formData.username || !formData.password) {
      displayErrorModal({ text: 'Please enter username and password' });
      return;
    }
    try {
      const response = await generateOTPApi({
        username: formData.username,
        password: formData.password,
      });
      if (response) {
        setSuccessTimestamp(Date.now());
        setCountdown(windowSize);
        if (!showOTP) {
          setShowOTP(true);
        }
        setFormData({ ...formData, otp: '' });
      }
    } catch (err) {
      setSuccessTimestamp();
      setShowOTP(false);
      setCountdown(0);
      displayErrorModal({ text: 'Something went wrong, Please try again' });
      console.error(err);
    }
  };

  const formSubmitHandler = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    setFormSubmitted(true);
    setIsLoading(true);

    if (formData.username.length && formData.password.length) {
      try {
        if (!env.OTP_ENABLED) {
          const response = await login({
            username: formData.username,
            password: formData.password,
            otp: '',
          });
          setUserDataAction(response);
          navigate('/');
          return;
        }
        if (!showOTP) {
          await generateOTP();
          return;
        }
        const response = await login({
          username: formData.username,
          password: formData.password,
          otp: formData.otp,
        });
        setUserDataAction(response);
        navigate('/');
      } catch (err) {
        displayErrorModal({ text: 'Something went wrong, Please try again' });
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const onChangeHandler = (e) => {
    let { value } = e.target;

    if (e.target.name === 'username') {
      value = value.toLowerCase();
    }

    setFormData({ ...formData, [e.target.name]: value });
  };

  return (
    <Form noValidate onSubmit={formSubmitHandler}>
      <div className={style.topText}>
        <h4><b>Welcome Back</b></h4>
        {showOTP ? (
          <p>
            Please enter the verification code sent to
            {` ${formData.username}.`}
          </p>
        ) : (
          <p>Please login to your account</p>
        )}
      </div>
      {!showOTP ? (
        <>
          <Form.Group>
            <Form.Control
              type="text"
              value={formData.username}
              onChange={onChangeHandler}
              name="username"
              required
              placeholder="Username"
              minLength={10}
              isInvalid={!formData.username.length && formSubmitted}
              disabled={showOTP}
            />
            <Form.Control.Feedback type="invalid">
              Username is required
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="formPassword">
            <Form.Control
              type="password"
              value={formData.password}
              onChange={onChangeHandler}
              name="password"
              required
              placeholder="Password"
              isInvalid={!formData.password.length && formSubmitted}
              disabled={showOTP}
            />
            <FormControl.Feedback type="invalid">
              Password is required
            </FormControl.Feedback>
          </Form.Group>
        </>
      ) : (
        <Form.Group controlId="formOTP">
          <Form.Control
            type="text"
            value={formData.otp}
            onChange={onChangeHandler}
            name="otp"
            required
            placeholder="Enter OTP code"
            isInvalid={!formData.otp.length && formSubmitted}
          />
          <FormControl.Feedback>
            OTP code is required, please check your email inbox.
          </FormControl.Feedback>
        </Form.Group>
      )}
      <Form.Group className={style.forgotPasswordWrapper}>
        {showOTP && (
          <div>
            <Button
              variant="link"
              onClick={generateOTP}
              disabled={countdown > 0}
              className={style.resendButton}
            >
              Resend Code
              {countdown > 0 && ` ${formatSecondsToMMSS(countdown)}`}
            </Button>
          </div>
        )}
        <div className="ml-auto">
          <Link to="/reset_password">Forgot password?</Link>
        </div>
      </Form.Group>
      <Button
        variant="primary"
        type="submit"
        // eslint-disable-next-line max-len
        disabled={(showOTP && !formData.otp) || (!showOTP && (!formData.username || !formData.password)) || isLoading}
      >
        {isLoading ? (
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="mr-2"
          />
        ) : 'Login'}
      </Button>
    </Form>
  );
};

export default connect(
  null,
  { setUserDataAction: setUserData },
)(LoginForm);
